import { type ReactNode, type HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  variant?: 'default' | 'outlined' | 'elevated';
  hover?: boolean;
}

export function Card({
  children,
  className,
  variant = 'default',
  hover = false,
  ...props
}: CardProps) {
  const variants = {
    default: 'bg-white border border-slate-200 shadow-card',
    outlined: 'bg-white border border-slate-200',
    elevated: 'bg-white shadow-lg',
  };

  return (
    <div
      className={cn(
        'rounded-xl',
        variants[variant],
        hover && 'transition-shadow duration-200 hover:shadow-card-hover',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

// Card Header
interface CardHeaderProps {
  children: ReactNode;
  className?: string;
}

export function CardHeader({ children, className }: CardHeaderProps) {
  return (
    <div
      className={cn(
        'px-5 py-4 border-b border-slate-100',
        className
      )}
    >
      {children}
    </div>
  );
}

// Card Title
interface CardTitleProps {
  children: ReactNode;
  className?: string;
}

export function CardTitle({ children, className }: CardTitleProps) {
  return (
    <h3 className={cn('text-base font-semibold text-slate-800 font-display', className)}>
      {children}
    </h3>
  );
}

// Card Content
interface CardContentProps {
  children: ReactNode;
  className?: string;
}

export function CardContent({ children, className }: CardContentProps) {
  return <div className={cn('p-5', className)}>{children}</div>;
}

// Card Footer
interface CardFooterProps {
  children: ReactNode;
  className?: string;
}

export function CardFooter({ children, className }: CardFooterProps) {
  return (
    <div
      className={cn(
        'px-5 py-4 bg-slate-50 border-t border-slate-100 rounded-b-xl',
        className
      )}
    >
      {children}
    </div>
  );
}

// Attach sub-components
Card.Header = CardHeader;
Card.Title = CardTitle;
Card.Content = CardContent;
Card.Footer = CardFooter;
